'use client';

import { createContext, useContext, type ReactNode } from 'react';

/**
 * Host adapter contract for the micro-content surface — short
 * videos, quizzes, weekly reads, scenarios, etc. Each content kind
 * has its own player popup (video, quiz, interactive) and its own
 * rail variant; the data contract treats them all as
 * {@link MicroContentItem} discriminated by {@code kind}.
 *
 * <p>Backend: gobito {@code micro-portal-ws} serves this contract at
 * {@code /v1/public/{tenantId}/micro/*} (published content, content
 * types, learner progress/favorites/recommendations, poll tallies);
 * the portal's {@code createLmsMicroSource} adapts it via the
 * {@code /api/micro/*} BFF.
 *
 * <p>Namespaced sub-adapters: {@code content}, {@code categories},
 * {@code progress?}, {@code recommendations?}. Progress + recs are
 * optional — hosts without personalization or progress tracking omit
 * them.
 */

/** Discriminator for micro-content items. Values match the legacy
 *  {@code MicroContentType} enum. */
export type MicroContentKind =
  | 'SHORT_VIDEO'
  | 'QUIZ'
  | 'WEEKLY_READ'
  | 'INTERACTIVE'
  | 'SHORTS'
  | 'INFO_CARDS'
  | 'CHECKLIST'
  | 'AUDIO'
  | 'DOWNLOAD'
  | 'SCENARIO'
  | 'LEARNING_PATH'
  | 'POLL'
  | 'BUSINESS_STORY_CARDS';

/** URL-segment for each kind (e.g. {@code /micro/short-videos}). */
export const MICRO_KIND_BY_SLUG: Record<string, MicroContentKind> = {
  'short-videos': 'SHORT_VIDEO',
  quizzes: 'QUIZ',
  'weekly-reads': 'WEEKLY_READ',
  interactive: 'INTERACTIVE',
  shorts: 'SHORTS',
  'info-cards': 'INFO_CARDS',
  checklists: 'CHECKLIST',
  audio: 'AUDIO',
  downloads: 'DOWNLOAD',
  scenarios: 'SCENARIO',
  'learning-paths': 'LEARNING_PATH',
  polls: 'POLL',
  'business-story-cards': 'BUSINESS_STORY_CARDS',
};

export function isKnownMicroKindSlug(slug: string): boolean {
  return slug in MICRO_KIND_BY_SLUG;
}

/** Lean content shape. {@code body} is opaque — its schema varies
 *  per {@code kind} (video has a URL, quiz has questions, etc.). */
export interface MicroContentItem {
  id: number | string;
  kind: MicroContentKind;
  kindSlug: string;
  title: string;
  description?: string | null;
  coverImageUrl?: string | null;
  categoryId?: number | null;
  /** Compact card-meta extracted server-side from {@code body} so
   *  rails can render footers without fetching the whole body. */
  meta?: {
    duration?: string;
    cardCount?: number;
    readingMinutes?: number;
    questionCount?: number;
  };
  /** Opaque body — schema per-kind. Loaded for the player popup. */
  body?: unknown;
  /** Soft refs to a course/lesson the item is associated with.
   *  Powers the resume-refresher's matching. */
  relatedCourseSlug?: string | null;
  relatedLessonId?: string | null;
  /** Cross-cutting topic labels. Lowercased, deduped server-side. */
  topicTags?: string[];
}

/** Category summary for the listing-page filter pills. */
export interface MicroCategorySummary {
  id: number | string;
  name: string;
  description?: string | null;
  iconRef?: string | null;
}

/** Per-tenant type-tile data shown on the {@code /micro} index. */
export interface MicroContentTypeSummary {
  id: number | string;
  kind: MicroContentKind;
  kindSlug: string;
  displayName: string;
  description?: string | null;
  iconRef?: string | null;
  contentCount?: number;
}

/** Per-user progress on a single micro item. */
export interface MicroProgress {
  itemId: number | string;
  status: 'not-started' | 'in-progress' | 'completed';
  /** For multi-question quizzes / multi-card scenarios: how many
   *  answered correctly so far. */
  correctCount?: number;
  /** Total answerable units. */
  totalCount?: number;
  lastSeenAt?: string;
  completedAt?: string;
  /** Whether the learner favorited this item (flag on the progress row). */
  favorite?: boolean;
  /** Kind-specific result echo (checklist checked ids, quiz answers, poll
   *  choices) — lets a reopened player restore its state. */
  result?: Record<string, unknown>;
}

/** Pre-computed aggregate progress shown in {@code MicroStatsStrip}. */
export interface MicroProgressSummary {
  totalCompleted: number;
  totalInProgress: number;
  /** Aggregate score across all completed quizzes/scenarios. */
  averageScore?: number;
  /** Current streak of consecutive days with at least one
   *  completion. */
  streakDays?: number;
}

// =============================================================================
// Namespaces
// =============================================================================

export interface MicroContentNamespace {
  /** Paginated content listing. Empty {@code kind} returns all kinds. */
  list(opts: {
    kind?: MicroContentKind;
    category?: string | number;
    /** Topic tag (lowercased, as {@code MicroContentItem.topicTags} carries
     *  them) — the listing behind a tag chip in the short-video player. */
    tag?: string;
    /** Course id — only items whose {@code relatedCourseSlug} is this
     *  course (a course page's study aids). */
    course?: string;
    page?: number;
    pageSize?: number;
    q?: string;
    /** {@code latest} = publishedAt desc, {@code popular} = completion count
     *  desc; default = operator display order, then newest first. */
    sort?: 'latest' | 'popular';
    locale?: string;
    signal?: AbortSignal;
  }): Promise<{
    items: MicroContentItem[];
    pagination: { page: number; pageSize: number; pageCount: number; total: number };
    categories: Array<string | number>;
  }>;

  /** Single item with full {@code body}. Used by the player popup. */
  fetchById(id: string | number, signal?: AbortSignal): Promise<MicroContentItem | null>;

  /** Curated featured rail. */
  featured(opts?: { limit?: number; signal?: AbortSignal }): Promise<MicroContentItem[]>;

  /** Engagement-ranked popular rail. */
  popular(opts?: { limit?: number; signal?: AbortSignal }): Promise<MicroContentItem[]>;
}

export interface MicroCategoriesNamespace {
  list(opts?: { locale?: string; signal?: AbortSignal }): Promise<MicroCategorySummary[]>;
}

export interface MicroContentTypesNamespace {
  list(opts?: { locale?: string; signal?: AbortSignal }): Promise<MicroContentTypeSummary[]>;
}

/** Optional. Hosts without progress tracking omit. */
export interface MicroProgressNamespace {
  fetchItem(itemId: number | string, signal?: AbortSignal): Promise<MicroProgress | null>;
  /** All of the caller's rows in one read — cards decorate themselves
   *  (completed/in-progress badge, heart) without N per-item fetches.
   *  Empty for anonymous sessions. */
  fetchMine?(signal?: AbortSignal): Promise<MicroProgress[]>;
  fetchSummary(signal?: AbortSignal): Promise<MicroProgressSummary | null>;
  /** Mark progress for an item — interpretation depends on kind
   *  (video pos, quiz answers, scenario decision, etc.). */
  update(args: {
    itemId: number | string;
    status?: MicroProgress['status'];
    correctCount?: number;
    totalCount?: number;
    /** Kind-specific result payload, merged (not replaced) into the row's
     *  RESULT JSONB server-side — reflection answers, poll choices, scores. */
    result?: Record<string, unknown>;
  }): Promise<MicroProgress | null>;
}

/** Optional. Personalized recommendations. */
export interface MicroRecommendationsNamespace {
  fetch(opts?: {
    limit?: number;
    surface?: string;
    signal?: AbortSignal;
  }): Promise<MicroContentItem[]>;
}

/** Optional. User's favorited micro items for the
 *  {@code MicroFavoritesRail}. */
export interface MicroFavoritesNamespace {
  list(signal?: AbortSignal): Promise<MicroContentItem[]>;
  toggle(args: { itemId: number | string }): Promise<boolean>;
}

/** Aggregate tally for a POLL item. {@code counts} is keyed by the
 *  option {@code id}s from the poll body (options are id-keyed so a
 *  vote survives reordering / relabeling). */
export interface MicroPollResults {
  counts: Record<string, number>;
  voters: number;
}

/** Optional. POLL kind: aggregate-tally reads + vote writes. The
 *  {@code PollRenderer} fetches the tally before recording a vote and
 *  optimistically adds the learner's choice(s) on top, so results can
 *  render without a read-after-write race. Hosts without a poll
 *  backend omit — the renderer then records the vote locally only.
 *
 *  <p>Backend: tallies come from {@code GET .../micro/content/{id}/poll-results}
 *  (aggregated over voters' progress rows); a vote is a result-merge on
 *  the learner's own progress row, so a revote overwrites. */
export interface MicroPollsNamespace {
  /** Current aggregate distribution for one poll item. Resolves
   *  {@code null} when the tally is unavailable. */
  results(itemId: number | string, signal?: AbortSignal): Promise<MicroPollResults | null>;
  /** Record the learner's vote. {@code choices} carries the selected
   *  option id(s) — more than one only when the poll body sets
   *  {@code allowMultiple}. */
  vote(args: { itemId: number | string; choices: string[] }): Promise<void>;
}

// =============================================================================
// Adapter contract
// =============================================================================

export interface MicroSource {
  content: MicroContentNamespace;
  categories: MicroCategoriesNamespace;
  types: MicroContentTypesNamespace;
  progress?: MicroProgressNamespace;
  recommendations?: MicroRecommendationsNamespace;
  favorites?: MicroFavoritesNamespace;
  polls?: MicroPollsNamespace;
}

const MicroSourceContext = createContext<MicroSource | null>(null);

export function MicroSourceProvider({
  value,
  children,
}: {
  value: MicroSource;
  children: ReactNode;
}) {
  return <MicroSourceContext.Provider value={value}>{children}</MicroSourceContext.Provider>;
}

export function useMicroSource(): MicroSource | null {
  return useContext(MicroSourceContext);
}
